import { UseFormReturn } from 'react-hook-form';
import { MiscellaneousField } from '../../reportsTypes';
import {
  useConditionalEventdetectorDefinitionsFetch,
  useConditionalWatchdogDefinitionsFetch,
} from '@ais-ui/api';
import { useEffect, useMemo, useState } from 'react';
import type { WatchdogDefinition } from '@ais-ui/api-spec/watchdog';
import {
  FancyMultiSelect,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  SelectType,
} from '@ais-ui/ui-core';
import type { EventdetectorDefinition } from '@ais-ui/api-spec/eventdetector';

interface MultiSelectItemProps {
  fieldName: string;
  form: UseFormReturn;
  fieldInfo: MiscellaneousField;
}

export const MiscellaneousMultiSelectItem = ({
  fieldName,
  form,
  fieldInfo,
}: MultiSelectItemProps) => {
  const [selectables, setSelectables] = useState<SelectType[]>(
    fieldInfo.options as SelectType[],
  );

  const {
    data: watchdogDefinitions,
    isLoading: isLoadingWatchdogDefinitions,
    isError: isErrorWatchdogDefinitions,
  } = useConditionalWatchdogDefinitionsFetch(
    fieldInfo.fetchOptions === 'watchdog',
  );

  const {
    data: eventDefinitions,
    isLoading: isLoadingEventDefinitions,
    isError: isErrorEventDefinitions,
  } = useConditionalEventdetectorDefinitionsFetch(
    fieldInfo.fetchOptions === 'watchdog',
  );

  const isLoading = useMemo(() => {
    return isLoadingWatchdogDefinitions || isLoadingEventDefinitions;
  }, [isLoadingEventDefinitions, isLoadingWatchdogDefinitions]);

  useEffect(() => {
    if (isLoadingWatchdogDefinitions || isLoadingEventDefinitions) return;

    setSelectables((currentSelectables) => {
      const selectableExists = (id: string) =>
        currentSelectables.some((selectable) => selectable.value === id);

      const updatedSelectables = [...currentSelectables];

      watchdogDefinitions?.records?.forEach((record: WatchdogDefinition) => {
        if (record.name && !selectableExists(record.id ? record.id : '')) {
          updatedSelectables.push({
            label: record.name,
            value: record.id as string,
          });
        }
      });

      eventDefinitions?.records?.forEach((record: EventdetectorDefinition) => {
        if (record.name && !selectableExists(record.id ? record.id : '')) {
          updatedSelectables.push({
            label: record.name,
            value: record.id as string,
          });
        }
      });

      return updatedSelectables;
    });
  }, [
    watchdogDefinitions,
    eventDefinitions,
    isLoadingWatchdogDefinitions,
    isLoadingEventDefinitions,
  ]);

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem>
          <div className="flex justify-between">
            <FormLabel>
              {fieldInfo.name}: {fieldInfo.required && '*'}
            </FormLabel>
            {fieldInfo.help && (
              <InfoIconTooltip className="mr-2">
                {fieldInfo.help}
              </InfoIconTooltip>
            )}
          </div>
          <FormControl onChange={() => form.clearErrors()}>
            <FancyMultiSelect
              value={field.value}
              onChange={field.onChange}
              availableSelectables={selectables}
              placeholder={fieldInfo.placeholder}
              isLoading={isLoading}
            ></FancyMultiSelect>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
